import React, { useEffect, useState, Children, lazy } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { PhoneIcon, MailIcon, MapPinIcon, ClockIcon, SendIcon, CheckCircleIcon } from 'lucide-react';
interface ContactPageProps {
  onNavigate: (page: string) => void;
}
export function ContactPage({
  onNavigate
}: ContactPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 4000);
    setFormData({
      name: '',
      email: '',
      message: ''
    });
  };
  const contactCards = [{
    icon: PhoneIcon,
    label: 'CALL US',
    primary: '1-800-395-4459',
    secondary: '(202) 367-9965',
    href: 'tel:+18003954459',
    borderColor: 'border-brand-blue/30',
    iconColor: 'text-brand-blue'
  }, {
    icon: MailIcon,
    label: 'EMAIL US',
    primary: 'info@wheelaroundrentals.com',
    secondary: 'We reply within 24 hours',
    href: 'mailto:info@wheelaroundrentals.com',
    borderColor: 'border-brand-red/30',
    iconColor: 'text-brand-red'
  }, {
    icon: MapPinIcon,
    label: 'VISIT US',
    primary: '11705 Berry Rd Suite 301-9',
    secondary: 'Waldorf, MD 20602',
    href: 'https://maps.google.com/?q=11705+Berry+Rd+Suite+301-9+Waldorf+MD+20602',
    borderColor: 'border-emerald-400/30',
    iconColor: 'text-emerald-500'
  }, {
    icon: ClockIcon,
    label: 'BUSINESS HOURS',
    primary: '09:00 AM – 06:00 PM',
    secondary: 'Thursday – Friday',
    href: undefined,
    borderColor: 'border-brand-navy/20',
    iconColor: 'text-brand-navy'
  }];
  const stagger = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  return <main className="flex-grow w-full bg-brand-gray" data-id="element-973">
      <PageHeader title="Contact Us" breadcrumb="Contact Us" onNavigateHome={() => onNavigate('home')} data-id="element-974" />

      {/* Main Content */}
      <section className="py-20" data-id="element-975">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-976">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start" data-id="element-977">
            {/* Contact Form — Left */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} className="lg:col-span-3 bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-gray-100" data-id="element-978">
              <h2 className="text-2xl md:text-3xl font-bold text-brand-navy mb-2" data-id="element-979">
                Send Us a Message
              </h2>
              <p className="text-brand-charcoal/60 mb-8" data-id="element-980">
                Fill the form below with your details and leave us a message. We
                would get back to you in no time.
              </p>

              {isSubmitted ? <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="flex flex-col items-center justify-center py-16 text-center" data-id="element-981">
                  <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mb-6" data-id="element-982">
                    <CheckCircleIcon className="w-10 h-10 text-emerald-500" data-id="element-983" />
                  </div>
                  <h3 className="text-2xl font-bold text-brand-navy mb-2" data-id="element-984">
                    Message Sent!
                  </h3>
                  <p className="text-brand-charcoal/60" data-id="element-985">
                    Thank you for reaching out. We'll get back to you shortly.
                  </p>
                </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-986">
                  {/* Name */}
                  <div className="relative" data-id="element-987">
                    <label htmlFor="name" className={`absolute left-4 transition-all duration-200 pointer-events-none ${focusedField === 'name' || formData.name ? 'top-2 text-xs font-semibold text-brand-blue' : 'top-4 text-sm text-gray-400'}`} data-id="element-988">
                      Your Name
                    </label>
                    <input id="name" type="text" required value={formData.name} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} onFocus={() => setFocusedField('name')} onBlur={() => setFocusedField(null)} className="w-full pt-6 pb-3 px-4 bg-brand-gray/50 border-2 border-gray-200 rounded-xl text-brand-charcoal focus:border-brand-blue focus:bg-white focus:outline-none transition-all" data-id="element-989" />
                  </div>

                  {/* Email */}
                  <div className="relative" data-id="element-990">
                    <label htmlFor="email" className={`absolute left-4 transition-all duration-200 pointer-events-none ${focusedField === 'email' || formData.email ? 'top-2 text-xs font-semibold text-brand-blue' : 'top-4 text-sm text-gray-400'}`} data-id="element-991">
                      Your Email
                    </label>
                    <input id="email" type="email" required value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)} className="w-full pt-6 pb-3 px-4 bg-brand-gray/50 border-2 border-gray-200 rounded-xl text-brand-charcoal focus:border-brand-blue focus:bg-white focus:outline-none transition-all" data-id="element-992" />
                  </div>

                  {/* Message */}
                  <div className="relative" data-id="element-993">
                    <label htmlFor="message" className={`absolute left-4 transition-all duration-200 pointer-events-none ${focusedField === 'message' || formData.message ? 'top-2 text-xs font-semibold text-brand-blue' : 'top-4 text-sm text-gray-400'}`} data-id="element-994">
                      Message
                    </label>
                    <textarea id="message" required rows={5} value={formData.message} onChange={e => setFormData({
                  ...formData,
                  message: e.target.value
                })} onFocus={() => setFocusedField('message')} onBlur={() => setFocusedField(null)} className="w-full pt-6 pb-3 px-4 bg-brand-gray/50 border-2 border-gray-200 rounded-xl text-brand-charcoal focus:border-brand-blue focus:bg-white focus:outline-none transition-all resize-none" data-id="element-995" />
                  </div>

                  <button type="submit" className="w-full py-4 bg-brand-navy text-white rounded-xl font-bold text-lg hover:bg-brand-blue transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3 group" data-id="element-996">
                    Send Message
                    <SendIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-997" />
                  </button>
                </form>}
            </motion.div>

            {/* Map & Quick Info — Right */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="lg:col-span-2 space-y-6" data-id="element-998">
              {/* Map Embed */}
              <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100" data-id="element-999">
                <iframe title="WheelAround Rentals Location" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3118.5!2d-76.886!3d38.626!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89b7a0!2s11705+Berry+Rd+Waldorf+MD!5e0!3m2!1sen!2sus!4v1700000000000" width="100%" height="280" style={{
                border: 0
              }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="w-full" data-id="element-1000" />
                <div className="p-5" data-id="element-1001">
                  <p className="font-bold text-brand-navy text-lg" data-id="element-1002">
                    WheelAround Rentals
                  </p>
                  <p className="text-brand-charcoal/60 text-sm" data-id="element-1003">
                    11705 Berry Rd Suite 301-9, Waldorf MD 20602
                  </p>
                </div>
              </div>

              {/* Quick Contact Card */}
              <div className="bg-brand-navy rounded-3xl p-8 text-white" data-id="element-1004">
                <h3 className="text-xl font-bold mb-6" data-id="element-1005">
                  Get in Touch Directly
                </h3>
                <div className="space-y-5" data-id="element-1006">
                  <a href="tel:+18003954459" className="flex items-center gap-4 group" data-id="element-1007">
                    <div className="w-11 h-11 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-brand-red transition-colors" data-id="element-1008">
                      <PhoneIcon className="w-5 h-5" data-id="element-1009" />
                    </div>
                    <div data-id="element-1010">
                      <p className="text-white/50 text-xs font-medium" data-id="element-1011">
                        Toll-Free
                      </p>
                      <p className="font-bold text-lg" data-id="element-1012">1-800-395-4459</p>
                    </div>
                  </a>
                  <a href="tel:+12023679965" className="flex items-center gap-4 group" data-id="element-1013">
                    <div className="w-11 h-11 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-brand-red transition-colors" data-id="element-1014">
                      <PhoneIcon className="w-5 h-5" data-id="element-1015" />
                    </div>
                    <div data-id="element-1016">
                      <p className="text-white/50 text-xs font-medium" data-id="element-1017">Local</p>
                      <p className="font-bold text-lg" data-id="element-1018">(202) 367-9965</p>
                    </div>
                  </a>
                  <a href="mailto:info@wheelaroundrentals.com" className="flex items-center gap-4 group" data-id="element-1019">
                    <div className="w-11 h-11 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-brand-red transition-colors" data-id="element-1020">
                      <MailIcon className="w-5 h-5" data-id="element-1021" />
                    </div>
                    <div data-id="element-1022">
                      <p className="text-white/50 text-xs font-medium" data-id="element-1023">Email</p>
                      <p className="font-bold" data-id="element-1024">info@wheelaroundrentals.com</p>
                    </div>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Contact Info Cards */}
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16" data-id="element-1025">
            {contactCards.map((card, index) => {
            const isLink = !!card.href;
            return <motion.div key={index} variants={fadeUp} data-id="element-1026">
                  {isLink ? <a href={card.href} target={card.href!.startsWith('http') ? '_blank' : undefined} rel={card.href!.startsWith('http') ? 'noopener noreferrer' : undefined} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex items-center gap-5 hover:shadow-lg transition-all h-full block" data-id="element-1027">
                      <div className={`w-14 h-14 rounded-full border-2 ${card.borderColor} flex items-center justify-center shrink-0`} data-id="element-1028">
                        <card.icon className={`w-6 h-6 ${card.iconColor}`} data-id="element-1029" />
                      </div>
                      <div className="min-w-0" data-id="element-1030">
                        <p className="text-[11px] font-bold text-brand-charcoal/40 uppercase tracking-widest mb-1" data-id="element-1031">
                          {card.label}
                        </p>
                        <p className="font-bold text-brand-navy text-[15px] leading-tight break-words" data-id="element-1032">
                          {card.primary}
                        </p>
                        <p className="text-sm text-brand-charcoal/50 mt-0.5" data-id="element-1033">
                          {card.secondary}
                        </p>
                      </div>
                    </a> : <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex items-center gap-5 h-full" data-id="element-1034">
                      <div className={`w-14 h-14 rounded-full border-2 ${card.borderColor} flex items-center justify-center shrink-0`} data-id="element-1035">
                        <card.icon className={`w-6 h-6 ${card.iconColor}`} data-id="element-1036" />
                      </div>
                      <div className="min-w-0" data-id="element-1037">
                        <p className="text-[11px] font-bold text-brand-charcoal/40 uppercase tracking-widest mb-1" data-id="element-1038">
                          {card.label}
                        </p>
                        <p className="font-bold text-brand-navy text-[15px] leading-tight" data-id="element-1039">
                          {card.primary}
                        </p>
                        <p className="text-sm text-brand-charcoal/50 mt-0.5" data-id="element-1040">
                          {card.secondary}
                        </p>
                      </div>
                    </div>}
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>
    </main>;
}